import { z } from "zod";
import { idSchema, instantSchema } from "@amber/contracts";
import { withdrawalCauseSchema } from "./shared.js";

export const AVAILABILITY_CHANGES = [
  "available",
  "changed_by_publisher",
  "deleted_by_publisher",
  "unavailable",
] as const;
export const availabilityChangeSchema = z.enum(AVAILABILITY_CHANGES);

const position = z.number().int().nonnegative();
const generation = z.number().int().positive();

const changeCommon = {
  position: z.number().int().positive(),
  environment_generation: generation,
  communication_id: idSchema("com"),
  evidence_revision_id: idSchema("rev"),
  committed_at: instantSchema,
};

export const changeEntryRecordSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("published"),
    scope: z
      .looseObject({ cause: z.string().optional(), replaces: z.record(z.string(), z.array(z.string())).optional() })
      .nullable(),
    ...changeCommon,
  }),
  z.strictObject({
    kind: z.literal("withdrawn"),
    scope: z.strictObject({ cause: withdrawalCauseSchema, notice: z.string().min(1) }),
    ...changeCommon,
  }),
  z.strictObject({
    kind: z.literal("restored"),
    scope: z.strictObject({ notice: z.string().min(1) }),
    ...changeCommon,
  }),
  z.strictObject({
    kind: z.literal("invalidated"),
    scope: z.strictObject({
      notice: z.string().min(1),
      unit_ids: z.array(idSchema("unt")),
      replacement_pending: z.boolean(),
    }),
    ...changeCommon,
  }),
  z.strictObject({
    kind: z.literal("relationship"),
    scope: z.strictObject({ relation_id: z.union([idSchema("spl"), idSchema("cpy")]), notice: z.string().min(1) }),
    ...changeCommon,
  }),
  z.strictObject({
    kind: z.literal("reassessed"),
    scope: z.strictObject({
      notice: z.string().min(1),
      profile_ids: z.array(idSchema("prf")),
      trigger: z.string().min(1),
    }),
    ...changeCommon,
  }),
  z.strictObject({
    kind: z.literal("availability"),
    scope: z.strictObject({ source_version_id: idSchema("ver"), availability: availabilityChangeSchema }),
    ...changeCommon,
  }),
]);
export type ChangeEntryRecord = z.infer<typeof changeEntryRecordSchema>;

export const ENVIRONMENT_CAUSES = ["reset", "restore"] as const;
export const environmentCauseSchema = z.enum(ENVIRONMENT_CAUSES);

const cursorPositionSchema = z.strictObject({ generation, position });

const resync = z.string().min(1);

function expectedCursorOutcome(
  cursor: z.infer<typeof cursorPositionSchema>,
  environment: { generation: number; head_position: number; retained_from_position: number },
): "unrestorable" | "expired" | "valid" {
  if (cursor.generation !== environment.generation || cursor.position > environment.head_position)
    return "unrestorable";
  if (cursor.position < environment.retained_from_position) return "expired";
  return "valid";
}

export const environmentRecordSchema = z
  .strictObject({
    generation,
    started_at: instantSchema,
    cause: environmentCauseSchema,
    retained_from_position: position,
    head_position: position,
    cursor_examples: z.array(
      z.discriminatedUnion("outcome", [
        z.strictObject({ cursor: cursorPositionSchema, outcome: z.literal("valid") }),
        z.strictObject({ cursor: cursorPositionSchema, outcome: z.literal("expired"), resync }),
        z.strictObject({ cursor: cursorPositionSchema, outcome: z.literal("unrestorable"), resync }),
        z.strictObject({ cursor: z.string().min(1), outcome: z.literal("invalid"), resync }),
      ]),
    ),
    snapshot_example: z.strictObject({
      as_of_position: position,
      continuation_cursor: cursorPositionSchema,
      records: z.array(idSchema("rev")),
    }),
  })
  .refine((e) => e.retained_from_position <= e.head_position, "retained history starts after the head")
  .refine(
    (e) =>
      e.cursor_examples.every((c) => {
        if (typeof c.cursor === "string") return true;
        return c.outcome === expectedCursorOutcome(c.cursor, e);
      }),
    "a cursor example's outcome does not follow from the generation and retained history",
  )
  .refine(
    (e) =>
      e.snapshot_example.continuation_cursor.generation === e.generation &&
      e.snapshot_example.continuation_cursor.position === e.snapshot_example.as_of_position,
    "a snapshot continues from its own position in the current generation",
  );
export type EnvironmentRecord = z.infer<typeof environmentRecordSchema>;
